// Reference Shelf — a poster wall of the P0 "dual-coding" infographics. Each
// poster is a self-contained HTML-string builder in the same visual language as
// the verb-group boot diagrams (gradient cards, mono grid cells, red = the thing
// that changes / the trap, sage = the regular/default). Every poster links to its
// full handbook article so the gallery is both a memory aid and a table of
// contents. View signature mirrors the other views: (mountEl, params).
import { navigate } from "../router/router.js";

// ---- Poster 1: Ser vs Estar decision tree -------------------------------------
function serEstarPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Which "to be"? Follow the branch</p>
      <div class="flex flex-col items-center gap-2 text-sm">
        <div class="bg-white border-2 border-sage-300 rounded-lg px-4 py-2 font-bold text-sage-800">SER ou ESTAR?</div>
        <div class="text-sage-300 text-lg leading-none">▾</div>
        <div class="grid sm:grid-cols-2 gap-3 w-full">
          <div class="bg-white border border-sage-200 rounded-lg p-3">
            <p class="text-center font-bold text-sage-800 mb-1">SER</p>
            <p class="text-center text-xs text-sage-500 mb-2">essence — who/what it <i>is</i></p>
            <div class="font-mono text-xs grid grid-cols-1 gap-0.5 text-sage-700">
              <div><b class="text-sage-900">D</b>escription — É alto.</div>
              <div><b class="text-sage-900">O</b>ccupation — Sou médico.</div>
              <div><b class="text-sage-900">C</b>haracteristic — É amável.</div>
              <div><b class="text-sage-900">T</b>ime / date — São duas horas.</div>
              <div><b class="text-sage-900">O</b>rigin — É de Portugal.</div>
              <div><b class="text-sage-900">R</b>elationship — É a minha mãe.</div>
            </div>
            <p class="text-center text-xs text-sage-400 mt-2 italic">mnemonic: <b>DOCTOR</b></p>
          </div>
          <div class="bg-red-50 border border-red-200 rounded-lg p-3">
            <p class="text-center font-bold text-red-700 mb-1">ESTAR</p>
            <p class="text-center text-xs text-sage-500 mb-2">state — how/where it <i>is</i> right now</p>
            <div class="font-mono text-xs grid grid-cols-1 gap-0.5 text-sage-700">
              <div><b class="text-red-700">P</b>osition — Está sentado.</div>
              <div><b class="text-red-700">L</b>ocation — Está em casa.</div>
              <div><b class="text-red-700">A</b>ction (a + inf) — Está a comer.</div>
              <div><b class="text-red-700">C</b>ondition — Está doente.</div>
              <div><b class="text-red-700">E</b>motion — Está feliz.</div>
            </div>
            <p class="text-center text-xs text-sage-400 mt-2 italic">mnemonic: <b>PLACE</b></p>
          </div>
        </div>
        <p class="text-xs text-sage-500 mt-1 italic text-center max-w-md">Quick test: would it still be true next year? → <b>ser</b>. Could it change by tomorrow? → <b>estar</b>.</p>
      </div>
    </div>`;
}

// ---- Poster 2: Por vs Para map ------------------------------------------------
function porParaPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">One English "for", two Portuguese words</p>
      <div class="grid sm:grid-cols-2 gap-3 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="text-center font-bold text-sage-800 mb-2">POR <span class="font-normal text-xs text-sage-400">— looking back / through</span></p>
          <div class="font-mono text-xs space-y-1 text-sage-700">
            <div><b class="text-sage-900">cause / reason</b><br>Obrigado pela ajuda.</div>
            <div><b class="text-sage-900">exchange</b><br>Paguei 10 € pelo livro.</div>
            <div><b class="text-sage-900">duration</b><br>Estudei por duas horas.</div>
            <div><b class="text-sage-900">movement through</b><br>Caminho pelo parque.</div>
            <div><b class="text-sage-900">on behalf of</b><br>Faço-o por ti.</div>
          </div>
        </div>
        <div class="bg-red-50 border border-red-200 rounded-lg p-3">
          <p class="text-center font-bold text-red-700 mb-2">PARA <span class="font-normal text-xs text-sage-400">— looking forward / aim</span></p>
          <div class="font-mono text-xs space-y-1 text-sage-700">
            <div><b class="text-red-700">purpose / goal</b><br>Estudo para aprender.</div>
            <div><b class="text-red-700">recipient</b><br>É para ti.</div>
            <div><b class="text-red-700">deadline</b><br>Para segunda-feira.</div>
            <div><b class="text-red-700">destination</b><br>Parto para Lisboa.</div>
            <div><b class="text-red-700">opinion</b><br>Para mim, é fácil.</div>
          </div>
        </div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic text-center">Arrow test: <b>por</b> points <b>backward</b> (the motive); <b>para</b> points <b>forward</b> (the destination).</p>
    </div>`;
}

// ---- Poster 3: Gender-ending cheat sheet --------------------------------------
function genderPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Predict a noun's gender from its ending</p>
      <div class="grid sm:grid-cols-2 gap-3 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="text-center font-bold text-sage-800 mb-2">usually <span class="text-sage-500">o</span> (masculine)</p>
          <div class="flex flex-wrap gap-1.5 justify-center font-mono text-xs">
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-o · livro</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-or · amor</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-ema · problema</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-l · papel</span>
          </div>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="text-center font-bold text-sage-800 mb-2">usually <span class="text-sage-500">a</span> (feminine)</p>
          <div class="flex flex-wrap gap-1.5 justify-center font-mono text-xs">
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-a · casa</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-ção · canção</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-são · missão</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-dade · cidade</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-gem · viagem</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-2 py-1 rounded">-ude · juventude</span>
          </div>
        </div>
      </div>
      <div class="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
        <p class="text-center font-bold text-red-700 mb-2">⚠ the traps — learn these by heart</p>
        <div class="flex flex-wrap gap-1.5 justify-center font-mono text-xs">
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">o dia</span>
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">o problema</span>
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">o mapa</span>
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">o idioma</span>
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">a mão</span>
          <span class="bg-red-100 border border-red-300 text-red-700 px-2 py-1 rounded">a foto</span>
        </div>
        <p class="text-center text-xs text-sage-500 mt-2 italic">Greek-origin <b>-ma</b> words are masculine; <i>mão</i> &amp; <i>foto</i> end in -ão/-o but are feminine.</p>
      </div>
    </div>`;
}

// ---- Poster 4: Sound–spelling chart -------------------------------------------
function soundsPoster() {
  return `
    <div class="bg-gradient-to-br from-sage-50 to-white border border-sage-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Letter → sound (the ones that trip up English speakers)</p>
      <p class="text-xs text-sage-500 mb-1.5">The five vowels — they also nasalize (ã, õ)</p>
      <div class="grid grid-cols-5 gap-1.5 text-center font-mono text-sm mb-3">
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-900">a</b><br><span class="text-xs text-sage-500">ah</span></div>
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-900">e</b><br><span class="text-xs text-sage-500">eh/uh</span></div>
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-900">i</b><br><span class="text-xs text-sage-500">ee</span></div>
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-900">o</b><br><span class="text-xs text-sage-500">oh/oo</span></div>
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-900">u</b><br><span class="text-xs text-sage-500">oo</span></div>
      </div>
      <p class="text-xs text-sage-500 mb-1.5">The tricky consonants &amp; digraphs</p>
      <div class="grid sm:grid-cols-2 gap-x-4 gap-y-1 font-mono text-xs text-sage-700">
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">c</b> + e/i</span><span>soft <i>s</i> — cidade</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b>c</b> + a/o/u</span><span>hard <i>k</i> — casa</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">ç</b></span><span>soft <i>s</i> — almoço</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">g</b> + e/i</span><span><i>zh</i> — gelo</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b>g</b> + a/o/u</span><span>hard <i>g</i> — gato</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">j</b></span><span><i>zh</i> — jogo</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">h</b></span><span><b>silent</b> — hoje</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">nh</b></span><span><i>ny</i> — vinho</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">lh</b></span><span><i>ly</i> — filho</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">ch</b></span><span><i>sh</i> — chave</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">s</b> (final)</span><span><i>sh</i> — dois</span></div>
        <div class="flex justify-between border-b border-sand-100 py-0.5"><span><b class="text-red-700">r</b>r / r-</span><span>guttural — carro</span></div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic">Red letters are the ones that differ most from English. Master those and your accent jumps.</p>
    </div>`;
}

// ---- Poster 5: Accent-rules flowchart -----------------------------------------
function accentPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Does the word need a written accent?</p>
      <div class="flex flex-col gap-2 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="font-bold text-sage-800 mb-1">Step 1 — the two default rules (no accent needed)</p>
          <div class="font-mono text-xs space-y-1 text-sage-700">
            <div>ends in <b>a, e, o (+ m, s)</b> → stress the <b>2nd-to-last</b> · ca<b class="text-sage-900">sa</b>, fa<b class="text-sage-900">lam</b></div>
            <div>ends in <b>i, u or l, r, z</b> → stress the <b>last</b> · fa<b class="text-sage-900">lar</b>, ani<b class="text-sage-900">mal</b></div>
          </div>
        </div>
        <div class="text-sage-300 text-lg leading-none text-center">▾</div>
        <div class="bg-red-50 border border-red-200 rounded-lg p-3">
          <p class="font-bold text-red-700 mb-1">Step 2 — if the real stress breaks the rule, write the accent</p>
          <div class="font-mono text-xs grid sm:grid-cols-2 gap-x-4 gap-y-0.5 text-sage-700">
            <div>ca<b class="text-red-700">fé</b> · so<b class="text-red-700">fá</b></div>
            <div>in<b class="text-red-700">glês</b> · <b class="text-red-700">ár</b>vore</div>
            <div><b class="text-red-700">lá</b>pis · <b class="text-red-700">mé</b>dico</div>
            <div><b class="text-red-700">fá</b>cil · <b class="text-red-700">mú</b>sica</div>
          </div>
        </div>
      </div>
      <div class="grid grid-cols-3 gap-2 mt-3 text-center text-xs font-mono">
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-800">oxítonas</b><br>stress last<br><span class="text-sage-400">café, avó</span></div>
        <div class="bg-white border border-sage-200 rounded-lg p-2"><b class="text-sage-800">paroxítonas</b><br>stress 2nd-last<br><span class="text-sage-400">árvore, lápis</span></div>
        <div class="bg-red-50 border border-red-200 rounded-lg p-2"><b class="text-red-700">proparoxítonas</b><br>stress 3rd-last<br><span class="text-sage-400">always! médico</span></div>
      </div>
    </div>`;
}

// ---- Poster 6: Gostar construction diagram ------------------------------------
function gostarPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">"Gosto de" = I like — <i>gostar</i> always takes <b>de</b></p>
      <div class="bg-white border border-sage-200 rounded-lg p-4">
        <div class="flex flex-wrap items-end justify-center gap-2 font-mono text-sm">
          <div class="text-center">
            <div class="bg-sage-50 border border-sage-200 text-sage-500 px-3 py-1.5 rounded-lg">(Eu)</div>
            <p class="text-xs text-sage-400 mt-1">optional</p>
          </div>
          <div class="text-center">
            <div class="bg-sage-50 border border-sage-200 text-sage-700 px-3 py-1.5 rounded-lg">gosto</div>
            <p class="text-xs text-sage-400 mt-1">agrees with subject</p>
          </div>
          <div class="text-center">
            <div class="bg-red-100 border-2 border-red-300 text-red-700 px-3 py-1.5 rounded-lg font-bold">de</div>
            <p class="text-xs text-sage-400 mt-1">never drop it!</p>
          </div>
          <div class="text-center">
            <div class="bg-sage-50 border border-sage-200 text-sage-700 px-3 py-1.5 rounded-lg">do café / dos livros</div>
            <p class="text-xs text-sage-400 mt-1">the thing you like</p>
          </div>
        </div>
      </div>
      <div class="grid sm:grid-cols-2 gap-3 mt-3 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="font-bold text-sage-800 text-xs mb-1.5">gostar conjugates like any -ar verb</p>
          <div class="font-mono text-xs grid grid-cols-2 gap-x-3 gap-y-0.5 text-sage-700">
            <div>eu gosto</div><div>nós gostamos</div>
            <div>tu gostas</div><div>vós gostais</div>
            <div>ele gosta</div><div>eles gostam</div>
          </div>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="font-bold text-sage-800 text-xs mb-1.5"><i>de</i> fuses with the article</p>
          <div class="font-mono text-xs space-y-0.5 text-sage-700">
            <div>de + o → <b class="text-red-700">do</b> · Gosto do café.</div>
            <div>de + as → <b class="text-red-700">das</b> · Gosto das casas.</div>
            <div>an action → de + inf · Gosto de dançar.</div>
          </div>
        </div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic text-center">The person is the subject: <i>Eu gosto dos livros</i> = "I like the books." But the cousins <b>doer</b>, <b>apetecer</b> and <b>faltar</b> do invert — <i>Dói-me a cabeça</i>, <i>Apetece-me um café</i>.</p>
    </div>`;
}

// ---- Poster 7: Pretérito vs Imperfecto (photo vs film) ------------------------
function preteritoImperfectoPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Two past tenses — pick a <i>viewpoint</i>, not a time</p>
      <div class="grid sm:grid-cols-2 gap-3 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="text-center font-bold text-sage-800 mb-1">📷 PRETÉRITO</p>
          <p class="text-center text-xs text-sage-500 mb-2">a photo — one bounded, finished event</p>
          <div class="font-mono text-xs space-y-1 text-sage-700">
            <div><b class="text-sage-900">single done action</b><br>Comi às duas.</div>
            <div><b class="text-sage-900">sequence of events</b><br>Chegou, comeu e saiu.</div>
            <div><b class="text-sage-900">the interruption</b><br>…quando o telefone tocou.</div>
          </div>
          <div class="flex flex-wrap gap-1 justify-center mt-2 font-mono text-[11px]">
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-1.5 py-0.5 rounded">ontem</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-1.5 py-0.5 rounded">de repente</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-1.5 py-0.5 rounded">ontem à noite</span>
            <span class="bg-sage-50 border border-sage-200 text-sage-700 px-1.5 py-0.5 rounded">uma vez</span>
          </div>
        </div>
        <div class="bg-red-50 border border-red-200 rounded-lg p-3">
          <p class="text-center font-bold text-red-700 mb-1">🎬 IMPERFEITO</p>
          <p class="text-center text-xs text-sage-500 mb-2">a film rolling — ongoing, habitual, scenery</p>
          <div class="font-mono text-xs space-y-1 text-sage-700">
            <div><b class="text-red-700">background / scene</b><br>Era de noite e chovia.</div>
            <div><b class="text-red-700">habit / routine</b><br>Ia à praia todos os verões.</div>
            <div><b class="text-red-700">the thing interrupted</b><br>Eu lia quando…</div>
          </div>
          <div class="flex flex-wrap gap-1 justify-center mt-2 font-mono text-[11px]">
            <span class="bg-red-100 border border-red-300 text-red-700 px-1.5 py-0.5 rounded">sempre</span>
            <span class="bg-red-100 border border-red-300 text-red-700 px-1.5 py-0.5 rounded">muitas vezes</span>
            <span class="bg-red-100 border border-red-300 text-red-700 px-1.5 py-0.5 rounded">todos os dias</span>
            <span class="bg-red-100 border border-red-300 text-red-700 px-1.5 py-0.5 rounded">enquanto</span>
          </div>
        </div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic text-center">Together: the <b>imperfeito</b> paints the scene; the <b>pretérito</b> drops in the event that moves the story.</p>
    </div>`;
}

// ---- Poster 8: RID object-pronoun order ---------------------------------------
function pronounOrderPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">Stacking pronouns? They line up in <b>RID</b> order, then fuse</p>
      <div class="bg-white border border-sage-200 rounded-lg p-4">
        <div class="flex flex-wrap items-end justify-center gap-2 font-mono text-sm">
          <div class="text-center">
            <div class="bg-sage-50 border border-sage-200 text-sage-700 px-3 py-1.5 rounded-lg font-bold">R</div>
            <p class="text-xs text-sage-400 mt-1">reflexive<br>me te se nos vos se</p>
          </div>
          <div class="text-sage-300 text-lg leading-none pb-3">+</div>
          <div class="text-center">
            <div class="bg-red-100 border-2 border-red-300 text-red-700 px-3 py-1.5 rounded-lg font-bold">I</div>
            <p class="text-xs text-sage-400 mt-1">indirect<br>me te lhe nos vos lhes</p>
          </div>
          <div class="text-sage-300 text-lg leading-none pb-3">+</div>
          <div class="text-center">
            <div class="bg-sage-50 border border-sage-200 text-sage-700 px-3 py-1.5 rounded-lg font-bold">D</div>
            <p class="text-xs text-sage-400 mt-1">direct<br>o a os as</p>
          </div>
          <div class="text-sage-300 text-lg leading-none pb-3">+</div>
          <div class="text-center">
            <div class="bg-white border border-sage-300 text-sage-800 px-3 py-1.5 rounded-lg font-bold">verb</div>
          </div>
        </div>
      </div>
      <div class="grid sm:grid-cols-2 gap-3 mt-3 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="font-bold text-sage-800 text-xs mb-1.5">indirect + direct fuse</p>
          <div class="font-mono text-xs space-y-0.5 text-sage-700">
            <div>me + o → <b class="text-red-700">mo</b></div>
            <div>te + o → <b class="text-red-700">to</b></div>
            <div>lhe + o → <b class="text-red-700">lho</b></div>
            <div class="text-sage-400 mt-1 not-italic">nos + o → no-lo · vos + o → vo-lo</div>
          </div>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-3">
          <p class="font-bold text-sage-800 text-xs mb-1.5">where they sit</p>
          <div class="font-mono text-xs space-y-0.5 text-sage-700">
            <div><b class="text-sage-900">after</b> the verb (default) · Dá-mo.</div>
            <div><b class="text-red-700">before</b> after não/que/adverb · Não mo dá.</div>
            <div><b class="text-red-700">inside</b> the future/conditional · Dar-mo-ei.</div>
          </div>
        </div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic text-center">Indirect comes before direct, then the pair fuses — <b>mo</b>, <b>to</b>, <b>lho</b> — and clings to the verb (enclisis) unless não / que / an adverb pulls it forward.</p>
    </div>`;
}

// ---- Poster 9: WEIRDO subjunctive triggers ------------------------------------
function weirdoPoster() {
  return `
    <div class="bg-gradient-to-br from-sand-50 to-white border border-sand-200 rounded-xl p-4">
      <p class="text-xs text-sage-500 uppercase tracking-wide mb-3">What flips the verb into the subjunctive: <b>WEIRDO</b></p>
      <div class="grid sm:grid-cols-2 gap-2 text-sm">
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">W</b>ishes — querer / esperar que<br><span class="text-sage-500">Quero que estudes.</span>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">E</b>motions — alegrar-se / recear que<br><span class="text-sage-500">Alegro-me que venhas.</span>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">I</b>mpersonal — é necessário que<br><span class="text-sage-500">É importante que saibas.</span>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">R</b>ecommendations — pedir / aconselhar que<br><span class="text-sage-500">Peço-te que esperes.</span>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">D</b>oubt / Denial — duvidar / não crer que<br><span class="text-sage-500">Duvido que seja verdade.</span>
        </div>
        <div class="bg-white border border-sage-200 rounded-lg p-2.5 font-mono text-xs text-sage-700">
          <b class="text-red-700">O</b>xalá — oxalá (que)<br><span class="text-sage-500">Oxalá chova em breve.</span>
        </div>
      </div>
      <div class="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
        <p class="text-center font-bold text-red-700 text-xs mb-1.5">two subjects + <b>que</b> are the gate</p>
        <div class="font-mono text-xs text-center text-sage-700 space-y-0.5">
          <div>same subject → infinitive · Quero <b class="text-sage-900">ir</b>.</div>
          <div>different subject → conjuntivo · Quero que <b class="text-red-700">vás</b>.</div>
        </div>
      </div>
      <p class="text-xs text-sage-500 mt-3 italic text-center">Build it: take the <b>eu</b> present, drop -o, add the opposite vowel (-ar → -e, -er/-ir → -a). After <i>quando / se / logo que</i>, Portuguese uses the future subjunctive: <i>Quando puderes…</i></p>
    </div>`;
}

// Poster registry — id, title, subtitle, builder, and the article slug it links to.
const POSTERS = [
  { id: "ser-estar", title: "Ser vs Estar", subtitle: "Which \"to be\" to use", build: serEstarPoster, slug: "ser-vs-estar" },
  { id: "por-para", title: "Por vs Para", subtitle: "The two \"for\"s by function", build: porParaPoster, slug: "por-vs-para" },
  { id: "gender", title: "Noun Gender Endings", subtitle: "Predict el/la + the traps", build: genderPoster, slug: "gender-agreement-of-nouns" },
  { id: "sounds", title: "Sound–Spelling Chart", subtitle: "Letter → sound", build: soundsPoster, slug: "pronunciation-and-the-alphabet" },
  { id: "accents", title: "Accent Rules", subtitle: "Where the written accent goes", build: accentPoster, slug: "stress-and-written-accents" },
  { id: "gostar", title: "Gostar Construction", subtitle: "The \"gosto de\" pattern", build: gostarPoster, slug: "gostar-and-similar-verbs" },
  { id: "preterito-imperfeito", title: "Pretérito vs Imperfeito", subtitle: "Photo vs film — the aspect choice", build: preteritoImperfectoPoster, slug: "a2-preterito-vs-imperfeito" },
  { id: "pronoun-order", title: "Object-Pronoun Order (RID)", subtitle: "Stacking me/te/se + lo/la", build: pronounOrderPoster, slug: "object-pronouns" },
  { id: "weirdo", title: "Subjunctive Triggers (WEIRDO)", subtitle: "What flips into the subjunctive", build: weirdoPoster, slug: "b1-present-subjunctive" },
];

export function referenceView(app, params = {}) {
  app.innerHTML = `
    <section>
      <h2 class="text-2xl font-bold text-sage-800 mb-1">Reference Shelf</h2>
      <p class="text-sage-600 text-sm mb-6 max-w-2xl">Every key distinction as a one-glance poster. Pair the picture with the rule — that's how it sticks. Tap any poster to read the full guide.</p>
      <div class="grid lg:grid-cols-2 gap-5" id="poster-wall"></div>
    </section>`;

  const wall = app.querySelector("#poster-wall");
  POSTERS.forEach((p) => {
    const card = document.createElement("div");
    card.id = `poster-${p.id}`;
    card.className = "scroll-mt-24";
    card.innerHTML = `
      <div class="flex items-baseline justify-between mb-2">
        <h3 class="font-bold text-lg text-sage-800">${p.title}</h3>
        <button data-slug="${p.slug}" class="poster-link text-xs font-medium text-sage-500 hover:text-sage-800">Read the full guide →</button>
      </div>
      <p class="text-xs text-sage-500 mb-2">${p.subtitle}</p>
      ${p.build()}
    `;
    wall.appendChild(card);
  });

  app.querySelectorAll(".poster-link").forEach((btn) =>
    btn.addEventListener("click", () => navigate("article", { slug: btn.dataset.slug }))
  );

  // Deep-link support: #/reference?poster=gostar scrolls that poster into view.
  if (params.poster && app.querySelector(`#poster-${params.poster}`)) {
    app.querySelector(`#poster-${params.poster}`).scrollIntoView({ behavior: "smooth", block: "start" });
  } else {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}
